import React, { Fragment, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";

import NoData from "./components/NoData";
import Pagination from "./components/Pagination";
import AllTicketsLoading from "./AllTicketsLoading";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableRow from "./components/DraggableRow.jsx";
import "./style.scss";

/**
 * Tickets listing table
 * @param {Object} props
 * @param {Array} props.state
 * @param {Array} props.state.data
 * @param {Array} props.state.columns
 * @param {Object} props?.rowSelection
 * @param {Object} props?.columnVisibility
 * @param {Function} props?.handleRowSelection
 */
const Table = observer((props) => {
  const tableRef = useRef(null);
  const data = props?.state?.data || props?.data;
  const columns = props?.state?.columns || props.columns;
  const noData = !data.loading && !data?.data?.length;
  const showPagination = props.pagination !== false;
  const enableRowDnD = props?.rowDnD?.enabled;

  const [hasScrollbar, setHasScrollbar] = useState(false);

  const handleRowClick = (row) =>
    props?.handleRowClick ? props?.handleRowClick(row) : null;

  const getRowClasses = (row) =>
    props?.getRowClasses ? props?.getRowClasses(row) : "";

  const table = useReactTable({
    data: data.data,
    manualPagination: true,
    enableRowSelection: !!props?.rowSelection,
    enableColumnResizing: props.enableColumnResizing ?? true,
    columnResizeMode: "onChange",
    columns: columns,
    pageCount: data.pageCount,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: props.actions?.setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: props?.handleRowSelection,
    getSortedRowModel: getSortedRowModel(),
    enableSorting: props.enableSorting ?? true,
    manualSorting: true,
    onSortingChange: props.actions?.setSorting,
    state: {
      sorting: props?.data?.sorting,
      rowSelection: props?.rowSelection || {},
      columnVisibility: props?.columnVisibility,
      pagination: {
        pageIndex: data.pageIndex,
        pageSize: data.pageSize,
      },
    },
    meta: props?.context || {},
  });

  useEffect(() => {
    const checkScrollbar = () => {
      if (tableRef.current) {
        setHasScrollbar(
          tableRef.current.scrollHeight > tableRef.current.clientHeight
        );
      }
    };

    checkScrollbar();
    window.addEventListener("resize", checkScrollbar);

    return () => {
      window.removeEventListener("resize", checkScrollbar);
    };
  }, []);

    if (data?.loading) {
      return <AllTicketsLoading loading />;
    }

   const tableMarkup = (
    <Fragment>
      <div
        className={`table-container ${noData ? "d-none" : ""} ${
          hasScrollbar ? "hasScrollbar" : ""
        }`}
        ref={tableRef}
        style={props?.maxHeightOffset ? { "--table-max-height-offset": props?.maxHeightOffset } : undefined}
      >
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      style={{ position: "relative", width: header.getSize() }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            <div
                              className={
                                header.column.getCanSort()
                                  ? "cursor-pointer select-none"
                                  : ""
                              }
                              onClick={header.column.getToggleSortingHandler()}
                              title={
                                header.column.getCanSort()
                                  ? header.column.getNextSortingOrder() ===
                                    "asc"
                                    ? "Sort ascending"
                                    : header.column.getNextSortingOrder() ===
                                      "desc"
                                    ? "Sort descending"
                                    : "Clear sort"
                                  : undefined
                              }
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: " 🔼",
                                desc: " 🔽",
                              }[header.column.getIsSorted()] ?? null}
                            </div>
                          )}
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`resizer ${
                            header.column.getIsResizing() ? "isResizing" : ""
                          }`}
                        ></div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
           <tbody>
            {table.getRowModel().rows?.map((row, index) => {
              const rowClasses = `${
                row.getIsSelected() ? 'selected' : ''
              } ${getRowClasses(row)}`;

              const cells = row.getVisibleCells()?.map((cell) => (
                <td key={cell.id}>
                  <div className='td-cell'>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </td>
              ));

              if (enableRowDnD) {
                return (
                  <DraggableRow
                    key={row.id}
                    row={row}
                    index={index}
                    className={rowClasses}
                    onMove={props.rowDnD.onMove}
                    onClick={handleRowClick}
                  >
                    {cells}
                  </DraggableRow>
                );
              }

              return (
                <tr
                  key={row.id}
                  className={rowClasses}
                  onClick={(e) => {
                    if (e.target.type === 'checkbox') return;
                    handleRowClick(row);
                  }}
                >
                  {cells}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showPagination &&
        (noData ? (
          <NoData />
        ) : (
          <Pagination
            table={{
              ...table,
              to: data.to,
              from: data.from,
              loading: data.loading,
              totalRecords: data.total,
              pageIndex: data.pageIndex,
              pageCount: data.pageCount,
            }}
          />
        ))}
    </Fragment>
  );

  if (!enableRowDnD) {
    return tableMarkup;
  }

  return <DndProvider backend={HTML5Backend}>{tableMarkup}</DndProvider>;
});
export default Table;
